import { useMemo, useState, type KeyboardEvent } from "react";
import { addDoc, collection } from "firebase/firestore";
import { paths, type Customer } from "@clevis/shared-types";
import { Button, Input, Label, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { textMatches } from "../lib/filter";
import { useCustomerList, type CustomerRow } from "../hooks/useCustomerList";
import { AddressField, type Located } from "./AddressField";

const MAX_SHOWN = 50;

export function customerAddress(c: Pick<Customer, "addressLine1" | "city" | "state" | "zip">): string {
  const cityLine = [c.city, [c.state, c.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return [c.addressLine1, cityLine].filter(Boolean).join(", ");
}

// Type-to-search customer chooser with an inline "+ New customer". Built for
// companies with thousands of customers, where a plain <select> is unusable.
// Lives inside other forms, so the new-customer fields are not a nested <form>.
export function CustomerPicker({
  tenantId,
  value,
  valueName = "",
  onChange,
}: {
  tenantId: string;
  value: string;
  /** Name to show for `value` before the list has loaded. */
  valueName?: string;
  onChange: (customer: CustomerRow | null) => void;
}) {
  const { rows, loaded, error } = useCustomerList(tenantId);
  const [text, setText] = useState<string | null>(null); // null = not typing; show the selection
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const [creating, setCreating] = useState<null | { displayName: string; phone: string; email: string; addressLine1: string; city: string; state: string; zip: string }>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [loc, setLoc] = useState<Located>(null);

  const selectedName = rows.find((c) => c.id === value)?.displayName ?? valueName;
  const q = text ?? "";
  const matches = useMemo(() => {
    if (!open) return [];
    if (!q.trim()) return rows.slice(0, MAX_SHOWN + 1);
    const out: CustomerRow[] = [];
    for (const c of rows) {
      if (textMatches(q, c.displayName, c.company, c.phone, c.email, c.addressLine1, c.city)) {
        out.push(c);
        if (out.length > MAX_SHOWN) break;
      }
    }
    return out;
  }, [rows, q, open]);
  const shown = matches.slice(0, MAX_SHOWN);

  function pick(c: CustomerRow | null) {
    onChange(c);
    setText(null);
    setOpen(false);
  }

  function startCreate(name: string) {
    setCreating({ displayName: name.trim(), phone: "", email: "", addressLine1: "", city: "", state: "", zip: "" });
    setLoc(null);
    setOpen(false);
    setNote("");
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHi((h) => Math.min(h + 1, shown.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && open) {
      e.preventDefault(); // never submit the surrounding form from the search box
      if (hi < shown.length) pick(shown[hi]!);
      else if (q.trim()) startCreate(q);
    } else if (e.key === "Escape") {
      setOpen(false);
      setText(null);
    }
  }

  async function create() {
    if (!creating || !creating.displayName.trim()) {
      setNote("A customer needs a name.");
      return;
    }
    setBusy(true);
    setNote("");
    try {
      const customer: Customer = {
        displayName: creating.displayName.trim(),
        company: "",
        email: creating.email.trim(),
        phone: creating.phone.trim(),
        addressLine1: creating.addressLine1.trim(),
        city: creating.city.trim(),
        state: creating.state.trim(),
        zip: creating.zip.trim(),
        lat: loc?.lat ?? null,
        lng: loc?.lng ?? null,
        tags: [],
        note: "",
        archivedAt: null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      const ref = await addDoc(collection(db, paths.tenantCustomers(tenantId)), customer);
      setCreating(null);
      pick({ id: ref.id, ...customer });
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const field = (key: keyof NonNullable<typeof creating>, label: string, cls = "") => (
    <div className={cls}>
      <Label>{label}</Label>
      <Input
        value={creating?.[key] ?? ""}
        autoFocus={key === "displayName"}
        onChange={(e) => setCreating((c) => (c ? { ...c, [key]: e.target.value } : c))}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void create();
          }
        }}
      />
    </div>
  );

  if (creating) {
    return (
      <div className="space-y-2 rounded-lg border border-brandorange/40 bg-cream/40 p-2">
        <div className="text-xs font-semibold text-ink">New customer</div>
        <div className="grid grid-cols-2 gap-2">
          {field("displayName", "Name", "col-span-2")}
          {field("phone", "Phone")}
          {field("email", "Email")}
          <div className="col-span-2">
            <Label>Street</Label>
            <AddressField
              fill="street"
              value={creating.addressLine1}
              onChange={(addressLine1) => setCreating((c) => (c ? { ...c, addressLine1 } : c))}
              located={loc}
              onLocate={setLoc}
              onPick={(m) => setCreating((c) => (c ? { ...c, city: m.city || c.city, state: m.state || c.state, zip: m.zip || c.zip } : c))}
            />
          </div>
          {field("city", "City")}
          <div className="grid grid-cols-2 gap-2">
            {field("state", "State")}
            {field("zip", "Zip")}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" className="px-3 py-1 text-xs" busy={busy} onClick={() => void create()}>
            Add customer
          </Button>
          <Button type="button" variant="ghost" className="px-3 py-1 text-xs" onClick={() => setCreating(null)}>
            Cancel
          </Button>
          {note ? <span className="text-xs text-danger">{note}</span> : null}
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex gap-1">
        <Input
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          placeholder={loaded || value ? "Type a name, phone or address…" : "Loading customers…"}
          value={text ?? selectedName}
          onFocus={(e) => {
            e.target.select();
            setOpen(true);
            setHi(0);
          }}
          onBlur={() => {
            setOpen(false);
            setText(null);
          }}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            setHi(0);
          }}
          onKeyDown={onKey}
        />
        <Button
          type="button"
          variant="ghost"
          className="shrink-0 px-2.5"
          title="New customer"
          aria-label="New customer"
          onClick={() => startCreate(text ?? "")}
        >
          +
        </Button>
      </div>
      {value && text === null ? (
        <button type="button" className="mt-0.5 text-[11px] text-steel underline hover:text-ink" onClick={() => pick(null)}>
          Clear customer
        </button>
      ) : null}
      {open ? (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-auto rounded-lg border border-steel/30 bg-white shadow-lg">
          {error ? <div className="px-3 py-2 text-xs text-danger">Couldn't load customers: {error}</div> : null}
          {!loaded && !error ? <div className="px-3 py-2 text-xs text-steel">Loading customers…</div> : null}
          {shown.map((c, i) => (
            <button
              key={c.id}
              type="button"
              className={cn("block w-full px-3 py-1.5 text-left", i === hi ? "bg-brandorange/10" : "hover:bg-cream")}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setHi(i)}
              onClick={() => pick(c)}
            >
              <div className="truncate text-sm text-ink">{c.displayName}</div>
              <div className="truncate text-[11px] text-steel">
                {[c.company !== c.displayName ? c.company : "", c.phone, c.city].filter(Boolean).join(" · ") || " "}
              </div>
            </button>
          ))}
          {loaded && matches.length > MAX_SHOWN ? (
            <div className="px-3 py-1.5 text-[11px] text-steel">More than {MAX_SHOWN} match. Keep typing to narrow it down.</div>
          ) : null}
          {loaded && q.trim() && shown.length === 0 ? (
            <div className="px-3 py-1.5 text-xs text-steel">No customer matches "{q.trim()}".</div>
          ) : null}
          <button
            type="button"
            className={cn(
              "block w-full border-t border-steel/10 px-3 py-1.5 text-left text-sm text-brandorange-dark",
              hi === shown.length ? "bg-brandorange/10" : "hover:bg-cream",
            )}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => setHi(shown.length)}
            onClick={() => startCreate(q)}
          >
            + {q.trim() ? `Add "${q.trim()}" as a new customer` : "New customer"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
